import React from 'react'
import moment from 'helpers/moment'
import {Link, useHistory} from 'react-router-dom'
import clsx from 'clsx'

import { makeStyles, withStyles} from 'external/material'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Avatar from '@mui/material/Avatar'
import CheckIcon from '@mui/icons-material/Done'
import RateReviewIcon from '@mui/icons-material/RateReview'
import {faEdit, faCalendarAlt, faUser, faInfoCircle} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"

import {AssignedContentIcon, PreviewIcon} from 'resources/icons'
import {createPost} from 'helpers/command'
import RowSettings from './assignment-row-settings'
import AssignmentAssessments from '../assignment-assessment'
import UpdateAssignment from './update-assignment'
import EditStudentAssignment from './EditStudentAssignment'

const useStyles = makeStyles(bodyStyles)

export default function AssignmentsBody({context}) {

  const classes = useStyles()
  const history = useHistory()
  const {quizAssignments, translate, link} = context
  const groupsStudentCount = context.groups.reduce((acc, {id, students}) => {
    acc[id] = students.length
    return acc
  },{})

  function typeName (type) {
    const {name = ""} =  context.quizTypes.find(({id}) => id == type) || {}
    return name
  }

  async function handleOpenModify(assignment) {
    const canModify = await context.validateStatus(assignment)
    if (canModify) {
      context.updateState({quizAssignmentToModify: {$set: assignment}, quizDialogs: {['edit-assignment']: {$set: true}}})
    }
  }

  function formatDate (date) {
    return moment(date).format(context.languageId == 'es' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')
  }

  function dateCell ({start, finish}) {
    return (moment(start).isSame(finish, 'day'))
      ? formatDate(start)
      : `${formatDate(start)} - ${formatDate(finish)}`
  }

  function assessmentLink(assessment, action, sectionId) {
    return sectionId
      ? link({assessment, action, sectionId}, '/:languageId/:role/:page/:assessment/:action/:sectionId')
      : link({assessment, action}, '/:languageId/:role/:page/:assessment/:action')
  }

  async function handleEditClick (assignment, link) {
    const canModify = await context.validateStatus(assignment)
    if (canModify) {
      await createPost('quiz', 'updateAssessmentProperties')({id: assignment.assessment, properties: {...assignment.assessmentProperties, validated: false}})
      await context.updateState({quizEditFrom: {$set: '/:languageId/:role/:page/assigned'}, quizEditFromQuery: {$set: {...context.query}}})
      history.push(assessmentLink(assignment.assessment, 'edit', assignment.firstSection))
    }
  }

  function handleEditStudentAssignmentClick (assignment) {
    context.updateState(
      {quizAssignmentToModify: {$set: assignment}},
      {quizDialogs: {['edit-student-assignment']: {$set: true}}}
    )
  }

  function handlePreviewClick () {
    context.setState({quizPreviewFrom: '/:languageId/:role/:page/assigned', quizPreviewFromQuery: {...context.query}})
  }

  const standardsLink = assessment => link({assessment, sectionName: 'standards'}, '/:languageId/:role/:page/:tab/:assessment/:sectionName', {preserveQuery: true})

  /*
  React.useEffect(() => {
    if (context.quizAssignments.length > 1) {
      context.updateState(
        {quizAssignmentToModify: {$set: context.quizAssignments[1]}},
        {quizDialogs: {['edit-student-assignment']: {$set: true}}}
      )
    }
  }, [context.quizAssignments])
  */

  return (<TableBody>
    <AssignmentAssessments context={context} />
    <UpdateAssignment context={context} />
    <EditStudentAssignment context={context} />
    {quizAssignments.map(assignment => {

      const open = context.assignmentId == assignment.id
      const assignmentId = open ? undefined : assignment.id
      const expandHref = '#' + context.link({assignmentId}, '/:languageId/:role/:page/assigned/:assignmentId?', {preserveQuery: true}) 
      const {validated} = assignment.assessmentProperties || {}

      return (
      <React.Fragment key={assignment.id}>
      <TableRow className={clsx(classes.row, "assignment")}>
        <TableCell className={clsx(classes.cell, classes.title)} align="left" scope="row">
          <span className={classes.titleContainer}>
            <span className={classes.assessmentIcon}><AssignedContentIcon fill={validated ? '#3295ce' : '#ff3a3a'} /></span>
            <span className={clsx(classes.titleText, {validated})}>{assignment.title}</span>
          </span>
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.group)} align="left" scope="row">
          {assignment.groupName}
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.studentCount)} align="left" scope="row">
          {assignment.studentCount}/{groupsStudentCount[assignment.group]}
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.type)} align="left" scope="row">
          {translate(`${typeName(assignment.type)}-label`)}
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.date)} align="left" scope="row">
          {dateCell(assignment)}
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.status)} align="center" scope="row">
          {assignment.status.started > 0 && (
            <Box className={classes.statusWrapper}>
              <Avatar className={clsx(classes.statusDot, {pending: assignment.status.pending > 0})}>
                {assignment.status.pending > 0 ? <span>R</span> : <CheckIcon />}
              </Avatar>
            </Box>
          )}
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.actions)} align="right" scope="row">
          <div className={classes.links}>
            {assignment.startedCount < 1 && (
              <Tooltip title={translate('edit-assignment-tooltip')}>
                <div onClick={()=> handleOpenModify(assignment)} className={clsx(classes.link, classes.scheduleLink)}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
              </Tooltip>
            )}
            {assignment.startedCount < 1 && (
              <Tooltip title={translate('edit-assessment-tooltip')}>
                <div onClick={() => handleEditClick(assignment)} className={clsx(classes.link, classes.editLink)}>
                  <FontAwesomeIcon icon={faEdit} />
                </div>
              </Tooltip>
            )}
            {assignment.startedCount > 0 && (<>
              <Tooltip title={translate('review-assignment-assessments-tooltip')}>
                <a size="small" href={expandHref} className={clsx(classes.link, classes.reviewLink, {visible: assignment.startedCount > 0})}>
                  <RateReviewIcon />
                </a>
              </Tooltip>
              <Tooltip title={translate('edit-student-assignment-tooltip')}>
                <div onClick={() => handleEditStudentAssignmentClick(assignment)} className={clsx(classes.link, classes.editStudentAssignmentLink)}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
              </Tooltip>
            </>)}
            <Tooltip title={translate('standards-tooltip')}>
              <Link to={standardsLink(assignment.assessment)} className={classes.link}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Link>
            </Tooltip>
            <Tooltip title={translate('preview-tooltip')}>
              <Link onClick={handlePreviewClick} to={assessmentLink(assignment.assessment, 'preview', assignment.firstSection)} className={clsx(classes.link, classes.previewLink)}>
                <PreviewIcon />
              </Link>
            </Tooltip>
            <div className={classes.settingsLink}>
              <RowSettings {...{context, assignment, classes}} />
            </div>
          </div>
        </TableCell>
      </TableRow>
      </React.Fragment>
    )})}
  </TableBody>
  )
}


function bodyStyles(theme) {
  const verticalCentering = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  }

  return {
    row: {
      '&:hover': {
        backgroundColor: '#def2ff'
      },
    },
    titleContainer: {
      display: 'flex'
    },
    assessmentIcon: {
      ...verticalCentering,
      marginTop: -5,
      marginRight: 15
    },
    titleText: {
      ...verticalCentering,
      color: '#ff3a3a',
      "&.validated": {
        color: '#000'
      }
    },
    expand: {
      marginRight: 8,
      visibility: 'hidden',
      '&.visible': {
        visibility: 'visible'
      }
    },
    statusWrapper: {
      display: 'flex',
      justifyContent: 'center',
    },
    statusDot: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      textAlign: 'center',
      width: 20,
      height: 20,
      fontSize: '9pt',
      color: '#fff',
      backgroundColor: '#00bbe7',
      "& > span": {
        marginTop: 1,
        marginLeft: 1
      },
      "& > svg": {
        fontSize: '11.5pt'
      },
      "&.pending" : {
        backgroundColor: '#ff3a3a'
      }
    },
    links: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'flex-end'
    },
    link: {
      color: '#009dd8',
      fontSize: '16pt',
      marginRight: 20,
      width: 25,
      cursor: 'pointer',
      ...verticalCentering
    },
    sharedLink: {
      display: 'flex',
      marginRight: 20,
      color: '#4d586e'
    },
    sharedIcon: {
      ...verticalCentering,
      color:'#c4c4c4', 
      marginRight: 10,
      '&.shared': {
        color: '#ea8511'
      }
    },
    sharedText: {
      ...verticalCentering,
      cursor: 'default',
      textAlign: 'center',
      marginRight: 10,
      width: 50
    },
    moreSettingsButton: {
      color: '#a2a6b2',
      cursor: 'pointer'
    },
    studentAssessments: {
      padding: 0,
      backgroundColor: '#f5f9fa',
      '&.collapsed': {
        borderBottom: 0,
        paddingBottom: 0,
        paddingTop: 0,
      },
      '&.opened': {
        paddingTop: 0,
        paddingBottom: 20,
      }
    },
    studentHeading: {
      color: '#707789',
      fontWeight: 'bold'
    },
    studentCell: {
      borderBottom: 0,
      '&.points.pending': {
        color: '#e91010'
      },
      '&.actions': {
        display: 'flex',
        '& .actionsContainer': {
          display: 'flex',
          padding: '5px 7px',
          flexGrow: '1',
          justifyContent: 'flex-end'
        }
      }
    },
    statusChip: {
      color: "#fff",
      fontSize: '9pt',
      padding: '5px 7px',
      "&.pending-review": {
        backgroundColor: "#ef3a3a"
      },
      "&.published": {
        backgroundColor: "#43b869"
      },
      "&.not-published": {
        backgroundColor: "#00bbe7"
      },
      "&.started": {
        backgroundColor: "#00bbe7"
      }
    },
    studentLink: {
      color: '#009dd8',
      textDecoration: 'none',
      fontSize: '11pt',
      cursor: 'pointer',
      ...verticalCentering
    },

  }
}


